import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Landmark, Wallet, PiggyBank } from 'lucide-react';
import { format } from 'date-fns';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { BankReconciliation } from '../components/BankReconciliation';

function useBankAccounts() {
  return useQuery({ queryKey: ['accounts', 'bank', 'accounts'], queryFn: async () => { const { data } = await api.get('/api/accounts/bank/accounts'); return data as { code: string; name: string; balance: number }[]; } });
}

function useBankStatement(code: string, startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['accounts', 'bank', 'statement', code, startDate, endDate],
    queryFn: async () => { const params = new URLSearchParams({ accountCode: code }); if (startDate) params.append('startDate', startDate); if (endDate) params.append('endDate', endDate); const { data } = await api.get(`/api/accounts/bank/statement?${params}`); return data as any; },
    enabled: !!code,
  });
}

function useCashBook(date: string) {
  return useQuery({ queryKey: ['accounts', 'cash', 'book', date], queryFn: async () => { const { data } = await api.get(`/api/accounts/cash/book?date=${date}`); return data as any; } });
}

const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const ICON_MAP: Record<string, any> = { '1010': Wallet, '1020': Landmark, '1030': PiggyBank };

export function BankingPage({ defaultView = 'statement', defaultCode = '1020', title = 'Banking & Cash' }: { defaultView?: 'statement' | 'cashbook' | 'reconciliation'; defaultCode?: string; title?: string }) {
  const { data: bankAccounts, isLoading: loadingAccounts } = useBankAccounts();
  const [selectedCode, setSelectedCode] = useState(defaultCode);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cashDate, setCashDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [view, setView] = useState<'statement' | 'cashbook' | 'reconciliation'>(defaultView);

  const { data: statement, isLoading: loadingStatement } = useBankStatement(selectedCode, startDate, endDate);
  const { data: cashBook, isLoading: loadingCash } = useCashBook(cashDate);

  return (
    <div className="p-6 space-y-6">
      <div><h1 className="text-2xl font-semibold tracking-tight">{title}</h1><p className="text-sm text-muted-foreground mt-1">Bank statements, cash book and petty cash</p></div>
      <ModuleGuide
        summary="Balances are not copied manually from bank statements — they are the running ledger balance of posted receipts, payments, expenses and transfers."
        steps={['Receipt/cash in → debits the selected bank or cash account (balance increases).', 'Payment/expense → credits the bank or cash account (balance decreases).', 'Contra transfer → moves the amount out of one account and into another, keeping totals consistent.']}
        formulas={[
          { label: 'Closing balance', formula: 'Opening balance + Money in − Money out' },
          { label: 'Cash difference', formula: 'Counted cash − System expected cash' },
        ]}
      />

      {/* Account Balances */}
      {loadingAccounts ? <Skeleton className="h-24" /> : (
        <div className="grid grid-cols-3 gap-4">
          {bankAccounts?.map(acc => {
            const Icon = ICON_MAP[acc.code] || Landmark;
            return (
              <div key={acc.code} className="bg-card border rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:border-primary/50 transition" onClick={() => { setSelectedCode(acc.code); setView('statement'); }}>
                <div className="p-2 rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>
                <div><p className="text-xs text-muted-foreground">{acc.name}</p><p className={`text-lg font-bold ${acc.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(acc.balance)}</p></div>
              </div>
            );
          })}
        </div>
      )}

      {/* View Toggle */}
      <div className="flex gap-2 border-b">
        <button onClick={() => setView('statement')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${view === 'statement' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Bank/Account Statement</button>
        <button onClick={() => setView('cashbook')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${view === 'cashbook' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Daily Cash Book</button>
        <button onClick={() => setView('reconciliation')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${view === 'reconciliation' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Reconciliation</button>
      </div>

      {view === 'reconciliation' && (
        <div className="space-y-3">
          <div className="w-64">
            <Select
              label="Account to reconcile"
              name="recon-account"
              value={selectedCode}
              onChange={e => setSelectedCode(e.target.value)}
              options={(bankAccounts || []).map(a => ({ value: a.code, label: a.name }))}
            />
          </div>
          <BankReconciliation accountCode={selectedCode} />
        </div>
      )}

      {view === 'statement' && (
        <Card className="shadow-xs">
          <CardHeader title={`Statement: ${statement?.account?.name || selectedCode}`} action={
            <div className="flex items-center gap-2">
              <Select name="account" value={selectedCode} onChange={e => setSelectedCode(e.target.value)} options={(bankAccounts || []).map(a => ({ value: a.code, label: a.name }))} />
              <Input name="start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36" />
              <Input name="end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36" />
            </div>
          } />
          <CardBody className="p-0">
            {loadingStatement ? <Skeleton className="h-[300px] m-4" /> : (
              <div className="overflow-auto max-h-[400px]">
                <div className="px-4 py-2 bg-muted/30 text-sm flex justify-between"><span>Opening Balance: <strong>{fmt(statement?.openingBalance || 0)}</strong></span><span>Closing Balance: <strong>{fmt(statement?.closingBalance || 0)}</strong></span></div>
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-2">Date</th><th className="px-4 py-2">Narration</th><th className="px-4 py-2">Type</th><th className="px-4 py-2 text-right">Debit</th><th className="px-4 py-2 text-right">Credit</th><th className="px-4 py-2 text-right">Balance</th></tr></thead>
                  <tbody className="divide-y">
                    {statement?.entries?.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No entries for this period.</td></tr>}
                    {statement?.entries?.map((e: any, i: number) => (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-4 py-2">{new Date(e.date).toLocaleDateString('en-IN')}</td>
                        <td className="px-4 py-2 truncate max-w-[250px]">{e.narration}</td>
                        <td className="px-4 py-2 text-xs capitalize">{e.voucherType?.replace('_', ' ')}</td>
                        <td className="px-4 py-2 text-right text-green-600">{e.debit > 0 ? fmt(e.debit) : ''}</td>
                        <td className="px-4 py-2 text-right text-red-600">{e.credit > 0 ? fmt(e.credit) : ''}</td>
                        <td className="px-4 py-2 text-right font-medium">{fmt(e.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {view === 'cashbook' && (
        <Card className="shadow-xs">
          <CardHeader title={`Cash Book — ${cashDate}`} action={<Input name="cashDate" type="date" value={cashDate} onChange={e => setCashDate(e.target.value)} className="w-40" />} />
          <CardBody className="p-0">
            {loadingCash ? <Skeleton className="h-[300px] m-4" /> : (
              <div className="overflow-auto max-h-[400px]">
                <div className="px-4 py-2 bg-muted/30 text-sm flex justify-between"><span>Opening: <strong>{fmt(cashBook?.openingBalance || 0)}</strong></span><span>Cash In: <strong className="text-green-600">{fmt(cashBook?.totalCashIn || 0)}</strong></span><span>Cash Out: <strong className="text-red-600">{fmt(cashBook?.totalCashOut || 0)}</strong></span><span>Closing: <strong>{fmt(cashBook?.closingBalance || 0)}</strong></span></div>
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted/50 sticky top-0"><tr><th className="px-4 py-2">Time</th><th className="px-4 py-2">Narration</th><th className="px-4 py-2 text-right">Cash In</th><th className="px-4 py-2 text-right">Cash Out</th><th className="px-4 py-2 text-right">Balance</th></tr></thead>
                  <tbody className="divide-y">
                    {cashBook?.entries?.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No cash transactions for this date.</td></tr>}
                    {cashBook?.entries?.map((e: any, i: number) => (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-4 py-2 text-xs">{e.time ? new Date(e.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                        <td className="px-4 py-2 truncate max-w-[250px]">{e.narration}</td>
                        <td className="px-4 py-2 text-right text-green-600">{e.cashIn > 0 ? fmt(e.cashIn) : ''}</td>
                        <td className="px-4 py-2 text-right text-red-600">{e.cashOut > 0 ? fmt(e.cashOut) : ''}</td>
                        <td className="px-4 py-2 text-right font-medium">{fmt(e.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
